<template>
  <div id="user-list">
    <v-row class="mb-5"></v-row>

    <!-- list filters -->
    <v-card>
      <v-card-title>
        <v-row>
          <v-col>
            Search &amp; Filter
          </v-col>

          <v-col
            align="end"
            class="pb-0"
          >
            <v-tooltip
              v-if="$can('create', 'Discovery')"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  fab
                  x-small
                  elevation="4"
                  color="primary"
                  :to="{ name: 'discovery-create' }"
                  v-on="on"
                >
                  <v-icon>
                    {{ icons.mdiPlus }}
                  </v-icon>
                </v-btn>
              </template>
              <span>Add New Discovery</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-title>

      <!-- table -->
      <vuex-table
        :headers="tableColumns"
        :items="discoveries"
        :meta.sync="meta"
        :search.sync="search"
        :options="options"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': [5, 10, 25, 50, 100]
        }"
        @update:options="o => options = o"
      >
        <!-- name -->
        <template #[`item.title`]="{item}">
          <div class="d-flex align-center">
            <v-avatar
              :color="item.thumbUrl ? '' : 'primary'"
              :class="item.thumbUrl ? '' : 'v-avatar-light-bg primary--text'"
              size="32"
            >
              <v-img
                v-if="item.thumbUrl"
                :src="item.thumbUrl"
              ></v-img>
              <span
                v-else
                class="font-weight-medium"
              >{{ avatarText(item.title) }}</span>
            </v-avatar>

            <div class="d-flex flex-column ms-3">
              <router-link
                :to="{ name : 'discovery-view', params : { id : item.id } }"
                class="text--primary font-weight-semibold text-truncate cursor-pointer text-decoration-none"
              >
                {{ item.title | textEllipsis(120) }}
              </router-link>
              <small>{{ item.subtitle | textEllipsis(120) }}</small>
            </div>
          </div>
        </template>

        <!-- actions -->
        <template #[`item.actions`]="{item}">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="me-1"
                v-bind="attrs"
                :to="{ name: 'discovery-view', params: { id: item.id } }"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiEyeOutline }}
                </v-icon>
              </v-btn>
            </template>
            <span>View Discovery</span>
          </v-tooltip>

          <v-tooltip
            v-if="$can('update', 'Discovery')"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="me-1"
                v-bind="attrs"
                :to="{ name: 'discovery-edit', params: { id: item.id } }"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiPencil }}
                </v-icon>
              </v-btn>
            </template>
            <span>Edit Discovery</span>
          </v-tooltip>

          <v-tooltip
            v-if="$can('destroy', 'Discovery')"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="error"
                v-bind="attrs"
                :disabled="deleting"
                :loading="isDialogOpen"
                @click="selectDiscoveryToDelete(item)"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
              </v-btn>
            </template>
            <span>Delete Discovery</span>
          </v-tooltip>
        </template>
      </vuex-table>
    </v-card>

    <discovery-delete-dialog
      v-if="$can('destroy', 'Discovery')"
      :data="selectedDiscoveries"
      :deleting="deleting"
      :is-dialog-open.sync="isDialogOpen"
    ></discovery-delete-dialog>
  </div>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import { mdiDotsVertical, mdiEyeOutline, mdiPlus, mdiPencil, mdiDeleteOutline, mdiExportVariant } from '@mdi/js'

// eslint-disable-next-line object-curly-newline
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from '@vue/composition-api'

import { avatarText } from '@core/utils/filter'
import useDiscoveryList from './useDiscoveryList'
import DiscoveryDeleteDialog from './DiscoveryDeleteDialog.vue'

export default {
  components: {
    DiscoveryDeleteDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const isDialogOpen = ref(false)

    const deleting = computed(() => vm.$store.getters['discoveries/deleting'])
    const selectedDiscoveries = ref(null)
    const selectDiscoveryToDelete = discoveries => {
      selectedDiscoveries.value = discoveries
      isDialogOpen.value = !!discoveries
    }

    const {
      tableColumns,
      totalDiscoveryListTable,
      loading,

      meta,
      search,
      filters,
      options,
      roleFilter,

      setOptions,
      setFilters,

      discoveries,
      discoveryListTable,

      init,
      destroy,
    } = useDiscoveryList()

    onMounted(() => {
      init()
    })
    onUnmounted(() => {
      destroy()
    })

    return {
      discoveryListTable,
      tableColumns,
      totalDiscoveryListTable,

      loading,
      avatarText,

      setOptions,
      setFilters,

      discoveries,
      meta,
      filters,
      options,
      search,
      roleFilter,

      // icons
      icons: {
        mdiPlus,
        mdiPencil,
        mdiEyeOutline,
        mdiDotsVertical,
        mdiDeleteOutline,
        mdiExportVariant,
      },

      isDialogOpen,

      deleting,
      selectedDiscoveries,
      selectDiscoveryToDelete,
    }
  },
}
</script>

<style lang="scss">
@import '@core/preset/preset/apps/user.scss';
</style>
